import { useRouter } from 'next/router';
import { type ComponentProps, type ComponentPropsWithoutRef, Fragment, memo, type ReactNode, useMemo } from 'react';

import { EllipsisIcon } from '@air/design-icons';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { BreadcrumbsButton, type BreadcrumbsButtonProps } from './BreadcrumbsButton';
import { BreadcrumbsItem, type BreadcrumbsItemProps } from './BreadcrumbsItem';
import { BreadcrumbsLink, type BreadcrumbsLinkProps } from './BreadcrumbsLink';
import { BreadcrumbsSeparator } from './BreadcrumbsSeparator';

export const breadcrumbs = tailwindVariants({
  base: 'flex min-w-0 items-center gap-2 text-gray-9',
  defaultVariants: {
    size: 'medium',
  },
  variants: {
    size: {
      large: 'text-16',
      medium: 'text-14',
      small: 'text-12',
    },
  },
});

export type BreadcrumbsItemTypeButton = Pick<BreadcrumbsButtonProps, 'onClick' | 'prefix' | 'suffix'> & {
  label: ReactNode;
  type: 'button';
};

export type BreadcrumbsItemTypeItem = Pick<BreadcrumbsItemProps, 'prefix' | 'suffix'> & {
  label: ReactNode;
  type: 'item';
};

export type BreadcrumbsItemTypeLink = Pick<BreadcrumbsLinkProps, 'href' | 'prefix' | 'suffix'> & {
  label: ReactNode;
  type: 'link';
};

export type BreadcrumbsItemType = (BreadcrumbsItemTypeItem | BreadcrumbsItemTypeLink | BreadcrumbsItemTypeButton) &
  Pick<ComponentProps<'div'>, 'className'> & {
    id: string;
  };

export type BreadcrumbsVariants = VariantProps<typeof breadcrumbs>;

export type BreadcrumbsProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> &
  BreadcrumbsVariants & {
    compactClassName?: string;
    isCompact?: boolean;
    items: BreadcrumbsItemType[];
    separator?: ReactNode;
    visibleItemsAtEnd?: number;
  };

export const Breadcrumbs = memo(
  ({
    className,
    compactClassName,
    isCompact: _isCompact = false,
    items = [],
    separator = '/',
    size = 'medium',
    visibleItemsAtEnd = 1,
    ...restOfProps
  }: BreadcrumbsProps) => {
    const { push } = useRouter();
    const isCompact = _isCompact && items.length > 2;

    const renderItemElement = (item: BreadcrumbsItemType) => {
      switch (item.type) {
        case 'button':
          return (
            <BreadcrumbsButton
              className={item.className}
              data-item-id={item.id}
              key={`breadcrumbs-button-${item.id}`}
              onClick={item.onClick}
              prefix={item.prefix}
              suffix={item.suffix}
            >
              {item.label}
            </BreadcrumbsButton>
          );
        case 'item':
          return (
            <BreadcrumbsItem
              className={item.className}
              data-item-id={item.id}
              key={`breadcrumbs-item-${item.id}`}
              prefix={item.prefix}
              suffix={item.suffix}
            >
              {item.label}
            </BreadcrumbsItem>
          );
        case 'link':
          return (
            <BreadcrumbsLink
              className={item.className}
              data-item-id={item.id}
              href={item.href}
              key={`breadcrumbs-link-${item.id}`}
              prefix={item.prefix}
              suffix={item.suffix}
            >
              {item.label}
            </BreadcrumbsLink>
          );
        default:
          return null;
      }
    };

    // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
    const itemRenderer = (item: BreadcrumbsItemType) => {
      const isLastItem = items.indexOf(item) === items.length - 1;
      const element = renderItemElement(item);

      return isLastItem ? (
        element
      ) : (
        <Fragment key={`breadcrumbs-fragment-${item.id}`}>
          {element}
          <BreadcrumbsSeparator key={`breadcrumbs-separator-${item.id}`}>{separator}</BreadcrumbsSeparator>
        </Fragment>
      );
    };

    const filteredItems = useMemo(() => {
      if (isCompact) {
        return items.slice(-visibleItemsAtEnd);
      }

      return items;
    }, [isCompact, items, visibleItemsAtEnd]);

    const menuOptions = useMemo<DropdownMenuOption[]>(() => {
      return items.slice(1, items.length - visibleItemsAtEnd).map((item) => ({
        id: item.id,
        label: item.label,
        onSelect: () => {
          if (item.type === 'link') {
            push(item.href);
          }

          if (item.type === 'button') {
            item.onClick();
          }
        },
        type: 'item',
      }));
    }, [items, push, visibleItemsAtEnd]);

    return (
      <div className={breadcrumbs({ class: className, size })} {...restOfProps}>
        {isCompact && (
          <>
            {itemRenderer(items[0])}
            <DropdownMenu
              align="start"
              data-testid="MORE_ANCESTORS_DROPDOWN"
              sideOffset={8}
              trigger={
                <IconButton
                  appearance="ghost"
                  className={cn('text-gray-8', compactClassName)}
                  color="gray"
                  data-testid="MORE_ANCESTORS_BUTTON"
                  label="More ancestors"
                  size="small"
                >
                  <EllipsisIcon size="sm" />
                </IconButton>
              }
            >
              {renderDropdownItems({ options: menuOptions })}
            </DropdownMenu>
            <BreadcrumbsSeparator>{separator}</BreadcrumbsSeparator>
          </>
        )}
        {filteredItems.map(itemRenderer)}
      </div>
    );
  },
);

Breadcrumbs.displayName = 'Breadcrumbs';
